/*
 * MGB predictive search.
 *
 * A dedicated element rather than an extension of Dawn's <predictive-search>: that
 * one is a dropdown that closes on focusout, which is the opposite of what a
 * full-screen overlay needs. The parts worth keeping from it — debounce, abort,
 * a per-term cache and the combobox keyboard model — are reimplemented here.
 *
 * Results are server rendered through the Section Rendering API, so this file never
 * builds product markup; it moves HTML and manages focus.
 */
class MgbSearch extends HTMLElement {
  static MIN_CHARS = 2;
  static DEBOUNCE_MS = 180;
  static RESULTS_PER_TYPE = 8;

  constructor() {
    super();

    this.overlay = this.querySelector('[data-mgb-search-overlay]');
    this.input = this.querySelector('[data-mgb-search-input]');
    this.results = this.querySelector('[data-mgb-search-results]');
    this.status = this.querySelector('[data-mgb-search-status]');
    this.clearButton = this.querySelector('[data-mgb-search-clear]');
    this.emptyState = this.querySelector('[data-mgb-search-empty]');

    this.cache = {};
    this.tracked = new Set();
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isOpen = false;
    this.trigger = null;
    this.debounceTimer = null;

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

    if (this.input) this.bind();
  }

  bind() {
    this.input.addEventListener('input', () => {
      this.toggleClear();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.onQueryChange(), MgbSearch.DEBOUNCE_MS);
    });

    this.input.form?.addEventListener('submit', (event) => {
      if (!this.query.length) event.preventDefault();
    });

    this.clearButton?.addEventListener('click', () => this.clear());

    this.querySelectorAll('[data-mgb-search-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    /* The backdrop is the element itself, so only a click that misses the panel closes. */
    this.overlay?.addEventListener('mousedown', (event) => {
      if (event.target === this.overlay) this.close();
    });

    this.addEventListener('keydown', (event) => this.onKeydown(event));

    /* Which result earned the click, and how far down it was. Capture phase, so it
       records even though the click is about to navigate away. */
    this.results?.addEventListener(
      'click',
      (event) => {
        const card = event.target.closest('.mgb-search-card');
        const link = event.target.closest('[data-mgb-search-result]');
        if (!card && !link) return;

        const options = this.options;
        const target = card || link;

        this.track('search_select', {
          search_term: this.searchTerm,
          result_type: card ? 'product' : 'link',
          position: card ? Number(card.dataset.position) || 0 : options.indexOf(target) + 1,
          destination: target.getAttribute('href') || '',
        });

        /* Stamped so a later add to cart can be attributed back to the search that
           found it. Reading it is a separate job — see the note in the requirements. */
        try {
          sessionStorage.setItem(
            'mgb_search_attribution',
            JSON.stringify({ term: this.searchTerm, at: Date.now() })
          );
        } catch (error) {
          /* Private mode; attribution is a nice-to-have, not a reason to fail. */
        }
      },
      true
    );

    /* Anything on the page can open this: header field, mobile bar, the nav drawer row. */
    document.querySelectorAll('[data-mgb-search-open]').forEach((opener) => {
      opener.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(opener);
      });
    });

    /* Popular terms are links so they work without JS, but a shopper who taps one
       is better served by results in the panel they already have open than by a
       page load. */
    this.addEventListener('click', (event) => {
      const term = event.target.closest('[data-mgb-search-term]');
      if (!term) return;

      event.preventDefault();
      this.input.value = term.dataset.mgbSearchTerm;
      this.toggleClear();
      this.input.focus();
      this.onQueryChange();
    });
  }

  get query() {
    return this.input.value.trim();
  }

  /*
   * One call, four destinations, all optional. GA4 carries the reporting, the web
   * pixel makes the event available to Shopify apps, Clarity tags the session so a
   * failed search can be watched back, and the DOM event is there for anything
   * added later without touching this file.
   */
  track(name, payload = {}) {
    const data = { ...payload, event_source: 'mgb_search' };

    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, data);
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...data });
      window.Shopify?.analytics?.publish?.(name, data);
      /* A tag rather than an event, so no-result sessions are filterable in Clarity. */
      if (typeof window.clarity === 'function' && data.search_term) {
        window.clarity('set', name, data.search_term);
      }
    } catch (error) {
      /* Analytics must never take the search down with it. */
    }

    this.dispatchEvent(new CustomEvent(`mgb:${name}`, { detail: data, bubbles: true }));
  }

  open(trigger) {
    if (this.isOpen) return;

    this.trigger = trigger || null;
    this.isOpen = true;
    this.setAttribute('open', '');
    document.body.classList.add('mgb-search-open');
    document.addEventListener('keydown', this.onDocumentKeydown);

    /* Focus after paint, or iOS declines to raise the keyboard. */
    requestAnimationFrame(() => {
      this.input.focus();
      this.input.setSelectionRange(this.input.value.length, this.input.value.length);
    });

    this.input.setAttribute('aria-expanded', 'true');

    this.track('search_open', {
      source: trigger?.dataset.mgbSearchIcon !== undefined ? 'header_icon' : trigger?.className || 'unknown',
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.removeAttribute('open');
    document.body.classList.remove('mgb-search-open');
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.input.setAttribute('aria-expanded', 'false');
    this.clearSelection();

    /* Send focus back where it came from, not to the top of the document. */
    this.trigger?.focus();
    this.trigger = null;
  }

  clear() {
    this.input.value = '';
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.renderResults('');
    this.toggleClear();
    this.input.focus();
  }

  toggleClear() {
    this.clearButton?.toggleAttribute('hidden', this.input.value.length === 0);
  }

  onQueryChange() {
    const term = this.query;

    if (term === this.searchTerm) return;
    this.searchTerm = term;

    if (term.length < MgbSearch.MIN_CHARS) {
      this.renderResults('');
      return;
    }

    this.getResults(term);
  }

  async getResults(term) {
    if (this.cache[term] !== undefined) {
      this.renderResults(this.cache[term]);
      return;
    }

    /* Drop the previous request so a slow early one cannot land on top of a fast
       later one and show results for a term the shopper has moved past. */
    this.abortController.abort();
    this.abortController = new AbortController();

    this.setAttribute('loading', '');

    const params = new URLSearchParams({
      q: term,
      section_id: 'mgb-search-results',
      'resources[type]': 'product,collection,query',
      'resources[limit]': MgbSearch.RESULTS_PER_TYPE,
      'resources[limit_scope]': 'each',
    });

    try {
      const response = await fetch(`${window.routes?.predictive_search_url || '/search/suggest'}?${params}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error(response.status);

      const html = await response.text();
      const section = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('#shopify-section-mgb-search-results');
      const markup = section ? section.innerHTML : html;

      this.cache[term] = markup;
      if (term === this.searchTerm) this.renderResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Never strand the shopper on a failed request — the full results page still works. */
      this.renderResults(
        `<div class="mgb-search__empty"><p class="mgb-search__empty-title">Search is unavailable right now</p>` +
          `<a class="mgb-search__empty-link" href="${((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')}search?q=${encodeURIComponent(term)}">Search everything for “${term}”</a></div>`
      );
    } finally {
      this.removeAttribute('loading');
    }
  }

  renderResults(markup) {
    this.results.innerHTML = markup;
    this.clearSelection();

    const hasResults = markup.length > 0;
    this.toggleAttribute('results', hasResults);
    this.emptyState?.toggleAttribute('hidden', hasResults);

    const count = this.results.querySelector('[data-mgb-search-count]');

    if (this.status) {
      this.status.textContent = count ? count.textContent.trim() : '';
    }

    if (!count || !this.searchTerm) return;

    /*
     * Fired here rather than on keystroke, so the term recorded is one the shopper
     * actually saw results for. Once per term per session — a shopper backspacing
     * through a word should not read as ten searches.
     *
     * This matters more than it looks: GA4's search_term data used to come from
     * shoppers landing on /search. Now that most of them will never leave the panel,
     * this event is what keeps that reporting alive.
     */
    if (this.tracked.has(this.searchTerm)) return;
    this.tracked.add(this.searchTerm);

    const total = parseInt(count.dataset.mgbSearchTotal, 10) || 0;

    this.track('search', {
      search_term: this.searchTerm,
      results: total,
      products: parseInt(count.dataset.mgbSearchProducts, 10) || 0,
    });

    if (total === 0) {
      /* The merchandising to-do list: what shoppers ask for and the store cannot answer. */
      this.track('search_no_results', { search_term: this.searchTerm });
    }
  }

  /* --- keyboard --- */

  get options() {
    return Array.from(this.results.querySelectorAll('[data-mgb-search-result], .mgb-search-card')).filter(
      (element) => element.offsetParent !== null
    );
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  onKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;

    const options = this.options;
    if (!options.length) return;

    if (event.key === 'Enter') {
      const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
      if (selected) {
        event.preventDefault();
        selected.click();
      }
      return;
    }

    event.preventDefault();

    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    let next = event.key === 'ArrowDown' ? current + 1 : current - 1;
    if (next >= options.length) next = 0;
    if (next < 0) next = options.length - 1;

    this.clearSelection();
    const option = options[next];
    option.setAttribute('aria-selected', 'true');
    if (!option.id) option.id = `mgb-search-option-${next}`;
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  clearSelection() {
    this.results.querySelectorAll('[aria-selected="true"]').forEach((option) => {
      option.removeAttribute('aria-selected');
    });
    this.input?.removeAttribute('aria-activedescendant');
  }
}

customElements.define('mgb-search', MgbSearch);

/*
 * Mobile handoff: while the search bar is on screen the header magnifier stays
 * invisible; once the bar scrolls off, the magnifier takes over. An observer rather
 * than a scroll handler — it cannot thrash around a threshold, and it costs nothing
 * on scroll.
 */
function initMgbSearchBarHandoff() {
  const bar = document.querySelector('[data-mgb-searchbar]');
  const icon = document.querySelector('[data-mgb-search-icon]');

  if (!bar || !icon) return;

  if (!('IntersectionObserver' in window)) {
    /* No observer: show the magnifier permanently rather than lose the entry point. */
    icon.classList.add('is-visible');
    return;
  }

  new IntersectionObserver(
    ([entry]) => icon.classList.toggle('is-visible', !entry.isIntersecting),
    { threshold: 0 }
  ).observe(bar);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMgbSearchBarHandoff);
} else {
  initMgbSearchBarHandoff();
}